import React, { useState } from 'react';
import { Box, Typography, Paper, IconButton, Tooltip } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

export interface HelpScreenshot {
  id: string;
  title: string;
  description: string;
  imagePath: string;
  animatedImagePath?: string; // Optional animated GIF path
  altText: string;
  steps: string[];
}

interface HelpScreenshotProps {
  screenshot: HelpScreenshot;
}

const HelpScreenshot: React.FC<HelpScreenshotProps> = ({ screenshot }) => {
  const [showAnimated, setShowAnimated] = useState(false);
  const hasAnimated = !!screenshot.animatedImagePath;

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">
          {screenshot.title}
        </Typography>
        {hasAnimated && (
          <Tooltip title={showAnimated ? "Show static image" : "Show animated demo"}>
            <IconButton 
              size="small" 
              onClick={() => setShowAnimated(!showAnimated)}
              sx={{ color: 'primary.main' }}
            >
              {showAnimated ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      
      <Box sx={{ mb: 2 }}>
        <img 
          src={showAnimated && hasAnimated ? screenshot.animatedImagePath! : screenshot.imagePath} 
          alt={screenshot.altText}
          style={{
            maxWidth: '100%',
            height: 'auto',
            border: '1px solid #ddd',
            borderRadius: '4px',
            minHeight: '50px',
            backgroundColor: '#f0f0f0'
          }}
          onError={(e) => {
            console.error('Failed to load screenshot:', showAnimated ? screenshot.animatedImagePath : screenshot.imagePath);
            console.error('Error details:', e);
            (e.target as HTMLImageElement).style.display = 'none';
            // Show fallback text
            const fallbackDiv = document.createElement('div');
            fallbackDiv.textContent = `[Screenshot: ${screenshot.title}]`;
            fallbackDiv.style.padding = '20px';
            fallbackDiv.style.backgroundColor = '#f0f0f0';
            fallbackDiv.style.border = '1px solid #ddd';
            fallbackDiv.style.borderRadius = '4px';
            fallbackDiv.style.textAlign = 'center';
            fallbackDiv.style.color = '#666';
            (e.target as HTMLImageElement).parentNode?.insertBefore(fallbackDiv, (e.target as HTMLImageElement).nextSibling);
          }}
          onLoad={(e) => {
            console.log('Screenshot loaded successfully:', showAnimated ? screenshot.animatedImagePath : screenshot.imagePath);
            console.log('Image dimensions:', (e.target as HTMLImageElement).naturalWidth, 'x', (e.target as HTMLImageElement).naturalHeight);
          }}
        />
      </Box>
      
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {screenshot.description}
      </Typography>
      
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Steps:
        </Typography>
        {screenshot.steps.map((step, index) => (
          <Typography key={index} variant="body2" sx={{ mb: 1 }}>
            {index + 1}. {step}
          </Typography>
        ))}
      </Box>
    </Paper>
  );
};

export default HelpScreenshot; 